import { html, nothing, PropertyValueMap, TemplateResult, CSSResultGroup } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { live } from 'lit/directives/live.js';
import { nanoid } from 'nanoid';
import { SlotController } from '../../controllers/slot-controller';
import { BlocksSliderChangeEvent } from '../../events/blocks-slider-change';
import { MOBILE_BREAK_POINT } from '../../util/common-modal';
import { BlocksElement } from '../BlocksElement';
import '../field-note/field-note';
import '../input-field/input-field';
import '../skeleton/skeleton';
import type { SliderBehavior, SliderPosition, SliderStatus } from './slider.model';
import styles from './slider.styles';

const LOWER_SLIDER = 'LOWERSLIDER';
const UPPER_SLIDER = 'UPPERSLIDER';
const THUMB_SIZE = 24;

/**
 * @slot before - The component before content (generally used to display min)
 * @slot after - The component after content (generally used to display max)
 * @slot label - shows description to slider, generally like a heading.
 * @slot error - errorNote slot
 * @slot warning - warningNote slot
 * @slot field-note - fieldNote slot
 *
 * @fires bl-slider-change - emitted when slider value is changed
 *
 * @csspart base - The component's base wrapper.
 * @csspart part-label - The component's label slot.
 * @csspart output-container - The component's output-container wrapper.
 * @csspart part-before- The component's before slot.
 * @csspart label-container - The component's label container wrapper.
 * @csspart label-wrapper - The component's label wrapper element.
 * @csspart output-label - The component's output-label element.
 * @csspart input - The component's input element.
 * @csspart part-after - The component's after slot.
 * @csspart output - The component's output element.
 * @csspart after-text - The component's after text field.
 * @csspart before-text - The component's before text field.
 * @csspart part-after - The component's after slot.
 * @csspart part-before - The component's before slot.
 * @csspart range - The component's range wrapper.
 * @csspart input-range-one - The component's range one input element.
 * @csspart input-range-two - The component's range two input element.
 * @csspart range-color - The component's range color.
 * @csspart label-container-one - The component's range one label container.
 * @csspart range-one-label - The component's range one label field.
 * @csspart label-container-two - The component's range two label container.
 * @csspart range-two-label - The component's range two label field.
 * @csspart output-range-one - The component's range one output element.
 * @csspart output-range-two - The component's range two output element.
 * @csspart range-container - The component's range container field.
 * @csspart bottom-input-wrapper - The component's bottom input wrapper.
 * @csspart heading - The component's heading label element.
 * @csspart loader-wrapper - The component's loader wrapper.
 * @csspart loader - The component's skeleton loader element.
 * @csspart field-note-wrapper - The component's field-note wrapper.
 * @csspart part-field-note - The component's field-note slot.
 * @csspart warning-note - The component's warning-note wrapper.
 * @csspart part-warning - The component's warning slot.
 * @csspart error-note - The component's error-note wrapper.
 * @csspart part-error - The component's error slot.
 */
export class BlocksSlider extends BlocksElement {
  static formAssociated = true;static styles: CSSResultGroup = styles;
  private canAllowDispatch = false;
  /**
   * Disabled attribute
   */
  @property({ type: Boolean, reflect: true })
  disabled: boolean | undefined;

  /**
   * readonly attribute
   */
  @property({ type: Boolean })
  readonly: boolean | undefined;

  /**
   * loading attribute
   */
  @property({ type: Boolean })
  isLoading: boolean | undefined;

  /**
   * handleLabelUnit attribute
   */
  @property({ type: String })
  handleLabelUnit: string | undefined;

  /**
   * Min property for slider
   */
  @property({ type: Number })
  min = 0;

  /**
   * minValue property for range slider
   */
  @property({ type: Number })
  minValue: number | undefined;

  /**
   * Max property for slider
   */
  @property({ type: Number })
  max = 100;

  /**
   * maxValue property for range slider
   */
  @property({ type: Number })
  maxValue: number | undefined;

  /**
   * Step for slider
   */
  @property({ type: Number })
  step = 1;

  /**
   * boolean value to enable the input field next to the slider to show/set current value
   */
  @property({ type: Boolean })
  displayOutputField: boolean | undefined;

  /**
   * Property to set default value of slider
   */
  @property({ type: Number })
  value = 0;

  /**
   * Slide Label
   */
  @property({ type: String })
  label: string | undefined;

  /**
   * hide Label
   */
  @property({ type: Boolean })
  hideLabel: boolean | undefined;

  /**
   * Default Slider output field Label
   */

  @property({ type: String })
  defaultSliderPlaceholder = 'value';

  /**
   * Range Slider left output field Label
   */

  @property({ type: String })
  lowerSliderPlaceholder = 'min';

  /**
   * Range Slider right output field Label
   */

  @property({ type: String })
  upperSliderPlaceholder = 'max';

  /**
   * enable displaying current min and max as before and after value
   */
  @property({ type: Boolean })
  hideMinMaxLabel = false;

  /**
   * Fieldnote text
   */
  @property({ type: String })
  fieldNote: string | undefined;

  /**
   * Status of slider
   */
  @property({ type: String })
  status: SliderStatus;

  /**
   * Errornote text
   */
  @property({ type: String })
  errorNote: string | undefined;

  /**
   * warningNote text
   */
  @property({ type: String })
  warningNote: string | undefined;

  /**
   * Aria describedby
   * 1) Used to connect the field note in select field to the select menu for accessibility
   */
  @property({ type: String })
  ariaDescribedBy: string | undefined;

  /**
   * The unique id of the field
   */
  @property({ type: String })
  fieldId: string | undefined;

  /**
   * The unique id of the range slider min field
   */
  @property({ type: String })
  fieldIdMin: string | undefined;

  /**
   * The unique id of the range slider max field
   */
  @property({ type: String })
  fieldIdMax: string | undefined;

  /**
   * Displays value label above slider
   */
  @property({ type: Boolean })
  hidePositionValue = false;

  /**
   * Behavior property for slider
   */
  @property({ type: String })
  behavior: SliderBehavior;

  /**
   *  output position used to display bottom range slider when option is bottom
   */
  @property({ type: String })
  outputPosition: SliderPosition = 'right';

  /**
   * **Breakpoint below which range slider form switches to mobile view.Defaults to 768px.**
   */
  @property({ type: Number })
  mobileBreakPoint = MOBILE_BREAK_POINT;

  /**
   * Max Label
   */
  @property({ type: String })
  maxLabel = 'Max';

  /**
   * Min Label
   */
  @property({ type: String })
  minLabel = 'Min';

  /**
   * Label for setting the aria-label property for default Slider.
   */
  @property({ type: String })
  defaultAriaLabel: string | undefined;

  /**
   * Label for setting the aria-label property for lower range in Range Slider.
   */
  @property({ type: String })
  lowerRangeAriaLabel: string | undefined;

  /**
   * Label for setting the aria-label property for upper range in Range Slider.
   */
  @property({ type: String })
  upperRangeAriaLabel: string | undefined;

  /**
   * Label for setting the aria-label property for input-field in slider.
   */
  @property({ type: String })
  ariaLabelSliderOutput: string | undefined;

  /**
   * Label for setting the aria-label property for for upper slider input-field.
   */
  @property({ type: String })
  ariaLabelUpperSliderOutput: string | undefined;

  /**
   * Label for setting the aria-label property for for lower slider input-field.
   */
  @property({ type: String })
  ariaLabelLowerSliderOutput: string | undefined;

  /**
   * ID for the content in slot before. Set this ID if the content before the Slider needs to announced out by the screen reader.
   */
  @property({ type: String })
  prefixContentId: string | undefined;

  /**
   * ID for the content in slot after. Set this ID if the content after the Slider needs to announced out by the screen reader.
   */
  @property({ type: String })
  suffixContentId: string | undefined;

  /**
   * query the default slider
   */
  @query('.cat-c-slider__input')
  private defaultSlider: HTMLInputElement | undefined;
  /**
   * query the lowerslider/lower input of range slider
   */
  @query('.cat-c-slider__range-one')
  private lowerSlider: HTMLInputElement | undefined;

  /**
   * query the upperSlider/upper input of range slider
   */
  @query('.cat-c-slider__range-two')
  private upperSlider: HTMLInputElement | undefined;

  @query('.cat-c-slider__range-one-label-container')
  private lowerSliderLabelContainer: HTMLElement | undefined;

  @query('.cat-c-slider__range-two-label-container')
  private upperSliderLabelContainer: HTMLElement | undefined;

  @query('.cat-c-slider__range-color')
  private rangeColor: HTMLElement | undefined;

  @query('.cat-c-slider__label-wrapper')
  private defaultLabelWrapper: HTMLElement | undefined;

  /**
   * Input value for lower slider of range slider
   */
  @state()
  private lowerSliderValue: number | undefined;

  /**
   * Input value for upper slider of range slider
   */
  @state()
  private upperSliderValue: number | undefined;

  @state()
  private result: string | undefined;

  @state()
  private outputValue: number | undefined;

  @state()
  private outputValueLowerSlider: number | undefined;

  @state()
  private outputValueUpperSlider: number | undefined;

  /** To set mobile view below or equal to 375px screen resolution */
  @state()
  private isMobile: boolean | undefined;

  protected slots = new SlotController(this, 'before', 'after', 'label', 'error', 'warning', 'field-note');

  private clearSetTimeout?: ReturnType<typeof setTimeout>;
  private internals_ = this.attachInternals?.();
  private observer?: MutationObserver;

  private get hasFieldNoteSlot(): boolean {
    return this.slots.hasSlotted('field-note');
  }

  private get hasErrorSlot(): boolean {
    return this.slots.hasSlotted('error');
  }

  private get hasWarningSlot(): boolean {
    return this.slots.hasSlotted('warning');
  }

  // ID for range slider label
  private rangeSliderLabelId = nanoid();

  /**
   * Associates the control with a form element
   */
  get form(): HTMLFormElement | null {
    return this.internals_?.form;
  }
  /**
   * 1) If will execute when behavior is of type range.
   * 2) on change of handleLabelUnit when behavior is not range,execute code for label
   * 3) To make it dynamic,outputPosition when property changed from control and when switched to mobile view.
   */

  protected updated(changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    /* 1 */
    if (this.behavior != 'range') {
      if (changedProperties.has('min') || changedProperties.has('max')) {
        if (this.min > 0) {
          this.value = this.min;
        }
        this.outputValue = this.min;
      }
      if (changedProperties.has('value')) {
        this.updateValue();
      }
      this.internals_?.setFormValue(`${this.value}`);
    } else {
      if (changedProperties.has('min') || changedProperties.has('max')) {
        this.handleRangeSlider(this.minValue, LOWER_SLIDER);
        this.handleRangeSlider(this.maxValue, UPPER_SLIDER);
        this.doubleRange();
      }
      if (changedProperties.has('minValue') || changedProperties.has('maxValue')) {
        this.outputValueLowerSlider = this.minValue ? this.minValue : this.min;
        this.outputValueUpperSlider = this.maxValue ? this.maxValue : this.max;
        this.lowerSliderValue = this.minValue ? this.minValue : this.min;
        this.upperSliderValue = this.maxValue ? this.maxValue : this.max;
        this.handleRangeSlider(this.minValue, LOWER_SLIDER);
        this.handleRangeSlider(this.maxValue, UPPER_SLIDER);
        this.doubleRange();
        this.internals_?.setFormValue(JSON.stringify({ min: this.minValue, max: this.maxValue }));
      }
      /* 3 */
      if (
        (changedProperties.has('outputPosition') && this.outputPosition !== changedProperties.get('outputPosition')) ||
        changedProperties.has('isMobile')
      ) {
        this.handleRangeSlider(this.lowerSliderValue, LOWER_SLIDER);
        this.handleRangeSlider(this.upperSliderValue, UPPER_SLIDER);
        this.doubleRange();
      }
    }

    if (changedProperties.has('handleLabelUnit')) {
      /* 2 */
      if (this.behavior !== 'range') {
        this.setPercentageLabel();
        this.updateComplete.then(() => {
          this.syncTooltipWithHandle(this.defaultSlider!, this.defaultLabelWrapper!, this.value);
        });
      }
    }
  }
  /**
   * function to sets the background style of default sliders when it is dragged
   */
  private generateLinearGradient(value1: number, value2: number) {
    return `linear-gradient(var(--rtlGradientToRight, to right), var(--cat-theme-color-background-brand) 0%, var(--cat-theme-color-background-brand)
    ${value1}%, var(--cat-theme-color-background-strong)
    ${value2}%, var(--cat-theme-color-background-strong) 100%)`;
  }

  /**
   *
   * called when range-slider is used. It handles styling for labels.
   */
  private handleRangeSlider(min_max_selected: number | undefined, sliderType: string) {
    /**
   * 
   * if minValue and maxValue values lies within the range of slider, then assign the corresponding
    values to the values of range-slider. 
    If it does not lie within the range, then leave the slider values to the min and max values itself.
   */
    if (min_max_selected !== undefined && min_max_selected >= this.min && min_max_selected <= this.max) {
      if (sliderType === LOWER_SLIDER) {
        this.outputValueLowerSlider = this.lowerSliderValue = min_max_selected;
      } else {
        this.outputValueUpperSlider = this.upperSliderValue = min_max_selected;
      }
    } else {
      if (sliderType === LOWER_SLIDER) {
        this.lowerSliderValue = this.outputValueLowerSlider = this.min;
      } else {
        this.upperSliderValue = this.outputValueUpperSlider = this.max;
      }
    }
    this.updateComplete.then(() => {
      if (sliderType === LOWER_SLIDER) {
        this.syncTooltipWithHandle(this.lowerSlider!, this.lowerSliderLabelContainer!, this.lowerSliderValue!);
      } else {
        this.syncTooltipWithHandle(this.upperSlider!, this.upperSliderLabelContainer!, this.upperSliderValue!);
      }
    });
  }
  /**
   * 
   * function to set the position of labels when they become equal and handle z-index
     when they become equal.
  */
  private checkEquality(sliderType: string) {
    const lowerVal = this.lowerSliderValue;
    const upperVal: number | undefined = this.upperSliderValue;
    if (lowerVal === upperVal) {
      if (sliderType === UPPER_SLIDER && !this.upperSlider?.classList.contains('cat-c-slider__range-upper-active-thumb')) {
        this.upperSlider?.classList.add('cat-c-slider__range-upper-active-thumb');
        this.syncTooltipWithHandle(this.upperSlider!, this.upperSliderLabelContainer!, this.upperSliderValue!);
      } else if (sliderType === LOWER_SLIDER && !this.lowerSlider?.classList.contains('cat-c-slider__range-lower-active-thumb')) {
        this.lowerSlider?.classList.add('cat-c-slider__range-lower-active-thumb');
        this.syncTooltipWithHandle(this.lowerSlider!, this.lowerSliderLabelContainer!, this.lowerSliderValue!);
      }
    } else if (lowerVal !== upperVal) {
      if (this.upperSlider?.classList.contains('cat-c-slider__range-upper-active-thumb')) {
        this.upperSlider.classList.remove('cat-c-slider__range-upper-active-thumb');
      }
      if (this.lowerSlider?.classList.contains('cat-c-slider__range-lower-active-thumb')) {
        this.lowerSlider.classList.remove('cat-c-slider__range-lower-active-thumb');
      }
    }
  }
  /**
   * called when upper range  oninput event occurs
   * i.e when the element gets user input
   */
  private upperSliderOnInput = (e: Event) => {
    this.upperSliderLabelContainer?.classList.add('cat-c-slider__range-upper-active-tooltip');
    this.upperSliderValue = Number((e.target as HTMLInputElement).value !== '' ? (e.target as HTMLInputElement).value : '0');
    this.setUpperPercentageRangeLabel();
    this.checkEquality(UPPER_SLIDER);

    this.outputValueUpperSlider = this.upperSliderValue;

    if (this.canAllowDispatch) {
      this.emitSlideEvent({
        minValue: this.lowerSliderValue,
        maxValue: this.upperSliderValue
      });
    }
  };
  /**
   *  add style to upper sliders when dragged corresponding to its value.
   */
  private setUpperPercentageRangeLabel() {
    const upperVal = this.upperSliderValue;
    const lowerVal = this.lowerSliderValue;

    if (upperVal !== undefined && lowerVal !== undefined && upperVal < lowerVal) {
      this.upperSliderValue = lowerVal; //Here we are resetting upper slider to minimum/equal value
      this.canAllowDispatch = false;
    } else if (lowerVal !== upperVal) {
      this.syncTooltipWithHandle(this.upperSlider!, this.upperSliderLabelContainer!, this.upperSliderValue!);
      this.canAllowDispatch = true;
    }
    if (this.rangeColor) {
      const upperInitialValue = this.upperSlider?.max;
      this.rangeColor.style.marginInlineEnd =
        ((+upperInitialValue! - this.upperSliderValue!) / (+this.upperSlider!.max - +this.lowerSlider!.min)) * 100 + '%'; /* 5 */
      this.rangeColor.style.width =
        ((this.upperSliderValue! - this.lowerSliderValue!) / (+this.upperSlider!.max - +this.lowerSlider!.min)) * 100 + '%'; /* 5 */
    }
  }
  /**
   *  add style to lower sliders when dragged corresponding to its value.
   */
  private setLowerPercentageRangeLabel() {
    const upperVal = this.upperSliderValue;
    const lowerVal = this.lowerSliderValue;
    if (lowerVal !== undefined && upperVal !== undefined && lowerVal > upperVal) {
      this.lowerSliderValue = upperVal; //Here we are resetting lower slider to maximum/equal value
      this.canAllowDispatch = false;
    } else if (lowerVal !== upperVal) {
      this.syncTooltipWithHandle(this.lowerSlider!, this.lowerSliderLabelContainer!, this.lowerSliderValue!);
      this.canAllowDispatch = true;
    }
    if (this.rangeColor) {
      const lowerInitialValue = this.lowerSlider?.min;

      this.rangeColor.style.marginInlineStart =
        ((this.lowerSliderValue! - +lowerInitialValue!) / (+this.upperSlider!.max - +this.lowerSlider!.min)) * 100 + '%'; /* 5 */
      this.rangeColor.style.width =
        ((this.upperSliderValue! - this.lowerSliderValue!) / (+this.upperSlider!.max - +this.lowerSlider!.min)) * 100 + '%'; /* 5 */
    }
  }

  /**
   * called when user drags the lower slider of range (the left-side slider)
   */
  private lowerSliderOnInput = (evt: Event) => {
    this.lowerSliderLabelContainer?.classList.add('cat-c-slider__range-lower-active-tooltip');
    this.lowerSliderValue = Number((evt.target as HTMLInputElement).value !== '' ? (evt.target as HTMLInputElement).value : '0');
    this.setLowerPercentageRangeLabel();
    this.checkEquality(LOWER_SLIDER);
    this.outputValueLowerSlider = this.lowerSliderValue;
    if (this.canAllowDispatch) {
      this.emitSlideEvent({
        minValue: this.lowerSliderValue,
        maxValue: this.upperSliderValue
      });
    }
  };
  /**
   * function that dispatch event for range sliders
   */
  private emitSlideEvent(detailObj: { minValue?: number; maxValue?: number; value?: number }) {
    if (this.clearSetTimeout) {
      clearTimeout(this.clearSetTimeout);
    }
    this.clearSetTimeout = setTimeout(() => {
      clearTimeout(this.clearSetTimeout);
      this.dispatch({
        eventName: 'bl-slider-change',
        detailObj
      }) as BlocksSliderChangeEvent;
    }, 300);
  }

  /**
   * Set the default / on load configuration for range-sliders
   */

  private async doubleRange() {
    const lowerSlider: HTMLInputElement | undefined = this.lowerSlider; /* 1 */
    const upperSlider: HTMLInputElement | undefined = this.upperSlider; /* 1 */

    const upperInitialValue = upperSlider?.max;
    const lowerInitialValue = lowerSlider?.min;

    if (this.rangeColor) {
      this.rangeColor.style.marginInlineEnd =
        ((+upperInitialValue! - this.upperSliderValue!) / (+upperSlider!.max - +lowerSlider!.min)) * 100 + '%'; /* 5 */
      this.rangeColor.style.width = ((this.upperSliderValue! - this.lowerSliderValue!) / (+upperSlider!.max - +lowerSlider!.min)) * 100 + '%'; /* 5 */
      this.rangeColor.style.marginInlineStart =
        ((this.lowerSliderValue! - +lowerInitialValue!) / (+upperSlider!.max - +lowerSlider!.min)) * 100 + '%'; /* 5 */
    }
  }
  /**
   * called when mouseup event happens for lower slider.
   * set the lower slider label back to its original z-index and removes the z-index of lower-slider
   */
  private toggleZIndexOnLowerSlider = () => {
    if (this.upperSliderValue !== undefined && this.lowerSliderValue !== undefined && !(this.upperSliderValue - this.lowerSliderValue <= 3)) {
      this.lowerSliderLabelContainer?.classList.remove('cat-c-slider__range-lower-active-tooltip');
      if (this.upperSliderLabelContainer?.classList.contains('cat-c-slider__range-upper-active-tooltip')) {
        this.upperSliderLabelContainer.classList.remove('cat-c-slider__range-upper-active-tooltip');
      }
    }
    if (this.upperSliderValue !== this.lowerSliderValue) {
      if (this.lowerSlider?.classList.contains('cat-c-slider__range-lower-active-thumb')) {
        this.lowerSlider.classList.remove('cat-c-slider__range-lower-active-thumb');
      }
    }
  };
  /**
   * called when mouseup event happens for upper slider.
   * set the upper slider label back to its original z-index and removes the z-index of upper-slider
   */
  private toggleZIndexOnUpperSlider = () => {
    if (this.upperSliderValue !== undefined && this.lowerSliderValue !== undefined && !(this.upperSliderValue - this.lowerSliderValue <= 3)) {
      this.upperSliderLabelContainer?.classList.remove('cat-c-slider__range-upper-active-tooltip');
      if (this.lowerSliderLabelContainer?.classList.contains('cat-c-slider__range-lower-active-tooltip')) {
        this.lowerSliderLabelContainer.classList.remove('cat-c-slider__range-lower-active-tooltip');
      }
    }
    if (this.upperSliderValue !== this.lowerSliderValue) {
      if (this.upperSlider?.classList.contains('cat-c-slider__range-upper-active-thumb')) {
        this.upperSlider.classList.remove('cat-c-slider__range-upper-active-thumb');
      }
    }
  };

  connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener('resize', this.onResize);
    this.fieldId = this.fieldId || nanoid();
    this.fieldIdMin = this.fieldIdMin || nanoid();
    this.fieldIdMax = this.fieldIdMax || nanoid();
    this.onResize();
    this.onDirectionChange();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.onResize);
    this.observer?.disconnect();
  }

  /** This function is used to detect when the direction of the page changes from LTR to RTL ot RTL to LTR so that we can update the position of tooltip accordingly
   * 1) Create a MutationObserver instance
   * 2) Target the <html> element
   * 3) Configuration for the observer (observe changes to attributes)
   * 4) Start observing the target node for attribute changes
   */
  private onDirectionChange() {
    const handleDirAttributeChange = (mutationsList: MutationRecord[]) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'dir') {
          if (this.behavior === 'range') {
            this.syncTooltipWithHandle(this.lowerSlider!, this.lowerSliderLabelContainer!, this.lowerSliderValue!);
            this.syncTooltipWithHandle(this.upperSlider!, this.upperSliderLabelContainer!, this.upperSliderValue!);
          } else if (this.defaultLabelWrapper) {
            this.syncTooltipWithHandle(this.defaultSlider!, this.defaultLabelWrapper, this.value);
          }
        }
      }
    };
    this.observer = new MutationObserver(handleDirAttributeChange); // 1
    const targetNode = document.querySelector('html')!; // 2
    const config = { attributes: true }; // 3

    this.observer.observe(targetNode, config); // 4
  }

  /**
   * called when there is resize on window..called to set the position
   * of both tooltip label of range slider in sync with slider handle.This is because the slider
   * width changes and hence the position of handle also changes.
   */
  private onResize = () => {
    if (this.behavior === 'range') {
      if (this.lowerSlider && this.lowerSliderLabelContainer) {
        this.syncTooltipWithHandle(this.lowerSlider, this.lowerSliderLabelContainer, this.lowerSliderValue!);
      }
      if (this.upperSlider && this.upperSliderLabelContainer) {
        this.syncTooltipWithHandle(this.upperSlider, this.upperSliderLabelContainer, this.upperSliderValue!);
      }
      const width = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
      if (width < this.mobileBreakPoint && this.displayOutputField) {
        this.isMobile = true;
      } else {
        this.isMobile = false;
      }
    } else if (this.behavior !== 'range' && this.defaultLabelWrapper && this.defaultSlider) {
      this.syncTooltipWithHandle(this.defaultSlider, this.defaultLabelWrapper, this.value);
    }
  };
  /**
   *called when default slider become active i.e when user drags the slider.
   */
  private handleDefaultSliderChange(e: Event) {
    this.value = Number((e.target as HTMLInputElement).value !== '' ? (e.target as HTMLInputElement).value : '0');
    this.outputValue = this.value;
    this.updateValue();
    this.emitSlideEvent({ value: this.value });
  }

  /**
   *calculates percentage and update the value of slider label ,sets its position and dispatch the value
   */
  private updateValue = () => {
    const percentage = this.calculatePercentage(this.value);
    this.setPercentageLabel();

    if (this.defaultSlider) {
      this.defaultSlider.style.background = this.generateLinearGradient(percentage, percentage);
    }
  };
  /**
   * called when user sets the position of slider using input-field. when the value is lesser than min , rest the value to min..and when it is greater
   than max, reset it to max
   */
  private checkValue() {
    if (this.outputValue !== undefined && this.outputValue < this.min) {
      this.value = this.min;
    } else if (this.outputValue !== undefined && this.outputValue > this.max) {
      this.value = this.max;
    } else {
      if (this.step > 1) {
        this.calculateValueOnStep();
      } else if (this.outputValue !== undefined) {
        this.value = this.outputValue;
      }
    }
  }
  /**
   *function to update the slider value when step value greater 
   than one is given
   */
  private calculateValueOnStep() {
    let currValue = this.min + this.step;
    let prevValue = this.min;
    while (this.outputValue !== undefined && this.outputValue > currValue) {
      prevValue = currValue;
      currValue = prevValue + this.step;
    }

    if (
      this.outputValue !== undefined &&
      (this.outputValue - prevValue < currValue - this.outputValue || this.outputValue - prevValue === currValue - this.outputValue)
    ) {
      this.value = prevValue;
    } else if (this.outputValue === 0) {
      this.value = this.min;
    } else {
      this.value = currValue;
    }
  }
  /**
   *update default slider based on user-input from its corresponding input-field
   */
  private syncOutFieldToSliderField = (e: Event) => {
    if ((e.target as HTMLInputElement).value === '') {
      this.value = this.min;
    } else {
      this.outputValue = +(e.target as HTMLInputElement).value;
      this.checkValue();
    }
    this.updateValue();
    this.emitSlideEvent({ value: this.value });
  };
  /**
   *update lower slider (left-side-slider) based on user-input from its corresponding input-field
   */
  private syncOutFieldToLowerRange = (e: Event) => {
    if ((e.target as HTMLInputElement).value === '' || +(e.target as HTMLInputElement).value < this.min) {
      this.lowerSliderValue = this.min;
    } else if (
      (this.upperSliderValue !== undefined && +(e.target as HTMLInputElement).value > this.upperSliderValue) ||
      +(e.target as HTMLInputElement).value > this.max
    ) {
      this.lowerSliderValue = this.upperSliderValue;
    } else {
      this.lowerSliderValue = +(e.target as HTMLInputElement).value;
    }
    this.setLowerPercentageRangeLabel();
    this.checkEquality(LOWER_SLIDER);
    if (this.canAllowDispatch) {
      this.emitSlideEvent({
        minValue: this.lowerSliderValue,
        maxValue: this.upperSliderValue
      });
    }
  };
  /**
   *update upper slider (right-side-slider) based on user-input from its corresponding input-field
   */
  private syncOutFieldToUpperRange = (e: Event) => {
    if ((e.target as HTMLInputElement).value === '' || +(e.target as HTMLInputElement).value > this.max) {
      this.upperSliderValue = this.max;
    } else if (
      (this.lowerSliderValue !== undefined && +(e.target as HTMLInputElement).value < this.lowerSliderValue) ||
      +(e.target as HTMLInputElement).value < this.min
    ) {
      this.upperSliderValue = this.lowerSliderValue;
    } else {
      this.upperSliderValue = +(e.target as HTMLInputElement).value;
    }

    this.setUpperPercentageRangeLabel();
    this.checkEquality(UPPER_SLIDER);
    if (this.canAllowDispatch) {
      this.emitSlideEvent({
        minValue: this.lowerSliderValue,
        maxValue: this.upperSliderValue
      });
    }
  };

  /**
   *Function that sets the position of tooltip [label] in sync with slider thumb.
   */
  private syncTooltipWithHandle = (slider: HTMLInputElement, wrapper: HTMLElement, sliderValue: number) => {
    if (!slider || !wrapper) {
      return;
    }
    const inputWidth = slider.offsetWidth;
    const tooltipWidth = wrapper.offsetWidth;
    const per = Math.max(0, (sliderValue - this.min) / (this.max - this.min));
    const percentAsWidth = inputWidth * per;
    const x = percentAsWidth - per * THUMB_SIZE;
    if (document.dir !== 'rtl') {
      wrapper.style.translate = `calc(${x - tooltipWidth / 2 + THUMB_SIZE / 2}px)`;
    } else {
      wrapper.style.translate = `calc(${(x - tooltipWidth / 2 + THUMB_SIZE / 2) * -1}px)`;
    }
  };

  /**
   * Dynamic styling on slider as a percentage. It sets the label value
   */
  private setPercentageLabel() {
    const percentage = this.calculatePercentage(this.value);
    if (this.defaultSlider) this.defaultSlider.style.background = this.generateLinearGradient(percentage, percentage);
    if (this.handleLabelUnit !== undefined) {
      this.result = this.value + this.handleLabelUnit;
    } else {
      this.result = `${this.value}`;
    }
    if (this.defaultLabelWrapper) {
      this.syncTooltipWithHandle(this.defaultSlider!, this.defaultLabelWrapper, this.value);
    }
  }

  /**
   * returns the percentage value.
   */
  private calculatePercentage(value: number) {
    return ((value - this.min) * 100) / (this.max - this.min);
  }

  private hasFieldNote() {
    return this.fieldNote || this.errorNote || this.warningNote || this.hasFieldNoteSlot || this.hasErrorSlot || this.hasWarningSlot;
  }

  private setAriaDescribedBy() {
    if (this.hasFieldNote()) {
      this.ariaDescribedBy = this.ariaDescribedBy || nanoid();
    }
  }

  /**To display max label*/
  private displayMaxLabel() {
    return html`<slot part="part-after" name="after" id=${ifDefined(this.suffixContentId)}>
      <div part="after-text" class="cat-c-slider-after-text">${this.max}</div></slot
    >`;
  }

  /**To display min label*/
  private displayMinLabel() {
    return html`<slot part="part-before" name="before" id=${ifDefined(this.prefixContentId)}>
      <div part="before-text" class="cat-c-slider-before-text">${this.min}</div></slot
    >`;
  }

  /**
   * Function to return with unit, if unit is present otherwise return value
   */
  private sliderValue(sliderValue: number) {
    if (this.handleLabelUnit) {
      return sliderValue + this.handleLabelUnit;
    }
    return sliderValue;
  }

  /**
   * To display Range Slider
   */
  private displaySliderRange() {
    return html` <div part="range" class="cat-c-slider__range">
      <input
        part="input-range-one"
        class="cat-c-slider__range-one cat-c-slider__range-input"
        type="range"
        tabindex=${ifDefined(this.readonly ? -1 : undefined)}
        min=${ifDefined(this.min)}
        max=${ifDefined(this.max)}
        aria-valuemax=${ifDefined(this.max)}
        aria-valuemin=${ifDefined(this.min)}
        aria-valuenow=${ifDefined(this.lowerSliderValue)}
        aria-valuetext=${this.sliderValue(this.lowerSliderValue ?? 0)}
        id="${ifDefined(this.fieldIdMin)}"
        @input=${this.lowerSliderOnInput}
        .value=${live(`${this.isLoading ? 25 : this.lowerSliderValue}`)}
        step=${ifDefined(this.step)}
        aria-label=${ifDefined(this.lowerRangeAriaLabel)}
        aria-labelledby=${ifDefined(!this.lowerRangeAriaLabel ? this.rangeSliderLabelId : undefined)}
        aria-describedby="${this.prefixContentId} ${this.ariaDescribedBy}"
        ?disabled="${this.isLoading ? true : this.disabled}"
        @mouseup=${this.toggleZIndexOnLowerSlider}
      />
      ${!(this.hidePositionValue || this.readonly || this.disabled)
        ? html`<span part="label-container-one" class="cat-c-slider__range-one-label-container"
            ><span part="range-one-label" class="cat-c-slider__range-one-label"> ${this.sliderValue(this.lowerSliderValue ?? 0)}</span></span
          >`
        : nothing}
      <span part="range-color" class="cat-c-slider__range-color"></span>
      <input
        part="input-range-two"
        class="cat-c-slider__range-two cat-c-slider__range-input"
        type="range"
        id="${ifDefined(this.fieldIdMax)}"
        tabindex=${ifDefined(this.readonly ? -1 : undefined)}
        min=${ifDefined(this.min)}
        max=${ifDefined(this.max)}
        aria-valuemax=${ifDefined(this.max)}
        aria-valuemin=${ifDefined(this.min)}
        aria-valuenow=${ifDefined(this.upperSliderValue)}
        aria-valuetext=${ifDefined(this.sliderValue(this.upperSliderValue ?? 0))}
        @input=${(e: Event) => this.upperSliderOnInput(e)}
        .value=${live(`${this.isLoading ? 75 : this.upperSliderValue}`)}
        step=${ifDefined(this.step)}
        ?disabled="${this.isLoading ? true : this.disabled}"
        aria-label=${ifDefined(this.upperRangeAriaLabel)}
        aria-labelledby=${ifDefined(!this.upperRangeAriaLabel ? this.rangeSliderLabelId : undefined)}
        aria-describedby="${this.suffixContentId} ${this.ariaDescribedBy}"
        @mouseup=${this.toggleZIndexOnUpperSlider}
      />
      ${!(this.hidePositionValue || this.readonly || this.disabled)
        ? html`<span part="label-container-two" class="cat-c-slider__range-two-label-container"
            ><span part="range-two-label" class="cat-c-slider__range-two-label"> ${this.sliderValue(this.upperSliderValue ?? 0)}</span></span
          >`
        : nothing}
    </div>`;
  }

  /**
   * To display Input fields for min and max
   */
  private displayInputFieldRange() {
    if (this.isLoading) {
      return;
    }
    return html`
      <cat-input-field
        part="output-range-one"
        exportparts=${this.getExportParts('input-field-output-one', 'input-field')}
        type="number"
        min=${ifDefined(this.min)}
        max=${ifDefined(this.max)}
        step=${ifDefined(this.step)}
        .value=${`${this.outputValueLowerSlider}`}
        class="cat-c-slider__output-range-one"
        @input=${this.syncOutFieldToLowerRange}
        placeholder="${this.lowerSliderPlaceholder}"
        ?disabled="${this.disabled}"
        ?readonly="${this.readonly}"
        arialabel="${ifDefined(this.ariaLabelLowerSliderOutput)}"
        ?hideStepper="${true}"
        ?hideLabel="${true}"
        .size="${'md'}"
        status="${ifDefined(this.status)}"
        .fieldNote=${this.status !== 'error' && this.status !== 'warning' ? this.minLabel : undefined}
      ></cat-input-field>
      <cat-input-field
        part="output-range-two"
        exportparts=${this.getExportParts('input-field-output-two', 'input-field')}
        type="number"
        min=${ifDefined(this.min)}
        max=${ifDefined(this.max)}
        step=${ifDefined(this.step)}
        .value=${`${this.outputValueUpperSlider}`}
        class="cat-c-slider__output-range-two"
        @input=${this.syncOutFieldToUpperRange}
        ?disabled="${this.disabled}"
        ?readonly="${this.readonly}"
        arialabel="${ifDefined(this.ariaLabelUpperSliderOutput)}"
        ?hideStepper="${true}"
        ?hideLabel="${true}"
        placeholder="${this.upperSliderPlaceholder}"
        .size="${'md'}"
        status="${ifDefined(this.status)}"
        .fieldNote=${this.status !== 'error' && this.status !== 'warning' ? this.maxLabel : undefined}
      ></cat-input-field>
    `;
  }

  /**
   * Display warning note from slot or from warningNote property
   */
  private getWarningNote() {
    return html`
      <cat-field-note
        part="field-note-wrapper"
        exportparts=${this.getExportParts('field-note')}
        id=${ifDefined(this.warningNote || this.hasWarningSlot ? this.ariaDescribedBy : undefined)}
        .status=${'warning'}
        class="cat-error-note--container cat-c-slider__warning-note-wrapper"
      >
        <slot part="part-warning" name="warning">${this.warningNote}</slot>
      </cat-field-note>
    `;
  }

  /**
   * Display error note from slot or from errorNote property
   */
  private getErrorNote() {
    return html`
      <cat-field-note
        part="field-note-wrapper"
        exportparts=${this.getExportParts('field-note')}
        id=${ifDefined(this.errorNote || this.hasErrorSlot ? this.ariaDescribedBy : undefined)}
        .status=${'error'}
        class="cat-error-note--container cat-c-slider__error-note-wrapper"
      >
        <slot part="part-error" name="error">${this.errorNote}</slot>
      </cat-field-note>
    `;
  }

  /**
   * Display field note from slot or from fieldNote property
   */
  private getFieldNote() {
    return html`
      <cat-field-note
        part="field-note-wrapper"
        exportparts=${this.getExportParts('field-note')}
        id=${ifDefined(this.fieldNote || this.hasFieldNoteSlot ? this.ariaDescribedBy : undefined)}
        class="cat-field-note--container cat-c-slider__field-note-wrapper"
      >
        <slot part="part-field-note" name="field-note">${this.fieldNote}</slot>
      </cat-field-note>
    `;
  }

  /**
   * returns the code of Range Slider with Bottom Output
   * @returns
   */
  private renderRangeSliderBottomOutput() {
    return html`
      ${this.isLoading ? this.renderLoading() : nothing}
      <slot part="part-label" name="label"
        ><label part="heading" class="cat-c-slider-heading" id=${this.rangeSliderLabelId}>${this.label}</label></slot
      >
      <div part="range-container" class="cat-c-slider__range-container">
        ${this.displayMinLabel()} ${this.displaySliderRange()} ${this.displayMaxLabel()}
      </div>
      <div part="bottom-input-wrapper" class="cat-c-slider-bottom-input-wrapper">
        ${this.displayOutputField ? html` ${this.displayInputFieldRange()} ` : nothing}
      </div>

      <!--- field note -->
      ${this.getFieldNote()}
      <!--- warning note -->
      ${this.getWarningNote()}
      <!--- error note -->
      ${this.getErrorNote()}
    `;
  }

  /**
   * returns the code of range slider
   */
  private renderRangeSlider() {
    if (this.outputPosition === 'bottom' || this.isMobile) {
      return this.renderRangeSliderBottomOutput();
    } else {
      return html`
        ${this.isLoading ? this.renderLoading() : nothing}
        <slot part="part-label" name="label"
          ><label part="heading" class="cat-c-slider-heading" id=${this.rangeSliderLabelId}>${this.label}</label></slot
        >
        <div part="range-container" class="cat-c-slider__range-container">
          ${this.displayMinLabel()} ${this.displaySliderRange()} ${this.displayMaxLabel()}
          ${this.displayOutputField ? html` ${this.displayInputFieldRange()} ` : nothing}
        </div>

        <!--- field note -->
        ${this.getFieldNote()}
        <!--- warning note -->
        ${this.getWarningNote()}
        <!--- error note -->
        ${this.getErrorNote()}
      `;
    }
  }

  /**
   * To render loading variant
   */
  private renderLoading() {
    return html` <div part="loader-wrapper">
      <cat-skeleton part="loader" exportparts=${this.getExportParts('skeleton')} class="cat-c-slider-heading" width="106px"></cat-skeleton>
    </div>`;
  }

  render(): TemplateResult<1> {
    const componentClassNames = this.componentClassNames('cat-c-slider', {
      'cat-c-slider--range': this.behavior === 'range',
      'cat-c-slider--output': this.displayOutputField,
      'cat-is-disabled': this.disabled,
      'cat-c-slider-error': this.status === 'error',
      'cat-c-slider-warning': this.status === 'warning',
      'cat-is-loading': this.isLoading,
      'cat-is-readonly': this.readonly,
      'cat-hidden-label': this.hideLabel,
      'cat-c-slider--has-after': !this.hideMinMaxLabel || this.slots.hasSlotted('after'),
      'cat-c-slider--has-before': !this.hideMinMaxLabel || this.slots.hasSlotted('before'),
      'cat-c-slider--has-warning': this.warningNote || this.hasWarningSlot,
      'cat-c-slider--has-error': this.errorNote || this.hasErrorSlot,
      'cat-c-slider--has-field-note': this.fieldNote || this.hasFieldNoteSlot,
      'cat-c-slider--has-label': !this.isLoading && (this.label || this.slots.hasSlotted('label'))
    });

    this.setAriaDescribedBy();
    return html`
      <div part="base" class="${componentClassNames}">
        ${this.behavior === 'range'
          ? this.renderRangeSlider()
          : html`
              ${this.isLoading ? this.renderLoading() : nothing}
              <slot part="part-label" name="label"
                ><label part="heading" class="cat-c-slider-heading" for="${ifDefined(this.fieldId)}">${this.label}</label></slot
              >
              <div part="output-container" class="cat-c-slider__output-container">
                ${this.displayMinLabel()}
                <div part="label-container" class="cat-c-slider__label-container">
                  <input
                    part="input"
                    ?disabled="${this.isLoading ? true : this.disabled}"
                    class="cat-c-slider__input"
                    type="range"
                    tabindex=${ifDefined(this.readonly ? -1 : undefined)}
                    min=${ifDefined(this.min)}
                    aria-valuemin=${ifDefined(this.min)}
                    max=${ifDefined(this.max)}
                    aria-valuemax=${ifDefined(this.max)}
                    step=${ifDefined(this.step)}
                    .value=${live(`${this.isLoading ? 50 : this.value}`)}
                    @input=${(e: Event) => this.handleDefaultSliderChange(e)}
                    id="${ifDefined(this.fieldId)}"
                    role="slider"
                    aria-label=${ifDefined(this.defaultAriaLabel)}
                    aria-describedby="${this.prefixContentId} ${this.suffixContentId} ${this.ariaDescribedBy}"
                    name="range"
                    aria-valuenow=${ifDefined(this.value)}
                    aria-valuetext=${this.sliderValue(this.value ?? 0)}
                  />
                  ${!(this.hidePositionValue || this.readonly || this.disabled)
                    ? html`
                        <span part="label-wrapper" class="cat-c-slider__label-wrapper"
                          ><span part="output-label" class="cat-c-slider__label"> ${this.result} </span>
                        </span>
                      `
                    : nothing}
                </div>
                ${this.displayMaxLabel()}
                ${!this.isLoading && this.displayOutputField
                  ? html`
                      <cat-input-field
                        part="output"
                        exportparts=${this.getExportParts('input-field-output', 'input-field')}
                        min=${ifDefined(this.min)}
                        max=${ifDefined(this.max)}
                        type="number"
                        @input=${this.syncOutFieldToSliderField}
                        step=${ifDefined(this.step)}
                        .value=${`${this.outputValue}`}
                        class="cat-c-slider__output"
                        ?disabled="${this.disabled}"
                        ?readonly="${this.readonly}"
                        ?hideStepper="${true}"
                        placeholder="${this.defaultSliderPlaceholder}"
                        arialabel="${ifDefined(this.ariaLabelSliderOutput)}"
                        ?hideLabel="${true}"
                        .size="${'md'}"
                        status="${ifDefined(this.status)}"
                      ></cat-input-field>
                    `
                  : nothing}
              </div>
              <!--- field note -->
              ${this.getFieldNote()}
              <!--- warning note -->
              ${this.getWarningNote()}
              <!--- error note -->
              ${this.getErrorNote()}
            `}
      </div>
    `;
  }
}

if (customElements.get('cat-slider') === undefined) {
  customElements.define('cat-slider', BlocksSlider);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-slider': BlocksSlider;
  }
}
